import styled from "@emotion/styled";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import { DesignToken, SpacingToken, Tab, TabsList, TabsWrapper, Text } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { SettingsRoutes } from "routes/settings/constants";
import ProviderAccordions, {
  Location,
} from "routes/settings/inboxes/forwarding-addresses/instructions-modal/ProviderAccordions";
import { selectLoggedInAgentCompletedWelcomeTutorialFlag } from "store/entities/agents";
import { selectDefaultMailboxAddress } from "store/entities/license";
import { WelcomeTutorialViewActions } from "store/views/welcome-tutorial";
import { withVar } from "themes/helpers";

import StepScreen from "../../components/StepScreen";
import { WelcomeTutorialRoutes, WelcomeTutorialStep } from "../../helpers";

import { ForwardingTab } from "./ForwardingTab";
import contactFormsTabImg from "./assets/contact-forms-tab-img.png";
import liveChatTabImg from "./assets/livechat-tab-img.png";
import openWidgetTabImg from "./assets/openwidget-tab-img.png";

enum Tabs {
  EMAIL_FORWARDING = "email-forwarding",
  CONTACT_FORMS = "contact-forms",
  LIVECHAT = "livechat",
  OPENWIDGET = "openwidget",
}

export const Forwarding: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const inboxAddress = useSelector(selectDefaultMailboxAddress);
  const currentAgentCompletedWelcomeTutorial = useSelector(selectLoggedInAgentCompletedWelcomeTutorialFlag);

  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.EMAIL_FORWARDING);

  const handleCopyAddress = (): void => {
    dispatch(WelcomeTutorialViewActions.mailboxCopied());
  };

  const completeTutorial = (keepTutorialOpened = false): void => {
    trackEngagement("Welcome tutorial: Finished", EventPlace.WelcomeTutorial, { sawForwardingStep: true });
    dispatch(WelcomeTutorialViewActions.finish({ keepWelcomeTutorialOpened: keepTutorialOpened }));
  };

  const handleNextClick = (): void => {
    if (!currentAgentCompletedWelcomeTutorial) {
      completeTutorial();
    } else {
      dispatch(WelcomeTutorialViewActions.keepTutorialOpened(false));
      history.push(WelcomeTutorialRoutes.FINISH);
    }
  };

  const handleBackClick = (): void => {
    trackEngagement("Welcome tutorial: Back from forwarding", EventPlace.WelcomeTutorial);
    history.push(WelcomeTutorialRoutes.INVITE);
  };

  const handleTabCtaClick = (
    source: "Create contact form clicked" | "Connect LiveChat clicked" | "Connect contact widget clicked"
  ): void => {
    dispatch(WelcomeTutorialViewActions.keepTutorialOpened(true));
    trackEngagement("Welcome Tutorial: Tabbed menu CTA clicked", EventPlace.WelcomeTutorial, { source });

    if (!currentAgentCompletedWelcomeTutorial) {
      completeTutorial(true);
    }
  };

  const tabs = [
    {
      id: Tabs.EMAIL_FORWARDING,
      title: "Email forwarding",
      onClick: (): void =>
        trackEngagement("Welcome Tutorial: tabbed menu email forwarding clicked", EventPlace.WelcomeTutorial),
      content: (
        <>
          <Flex column gap={withVar(SpacingToken.Spacing2)}>
            <Text customColor={withVar(DesignToken.ContentBasicPrimary)}>Select your email provider</Text>
            <Flex column>
              <ProviderAccordions
                inboxAddress={inboxAddress}
                onForwardingAddressCopy={handleCopyAddress}
                location={Location.WelcomeTutorial}
              />
            </Flex>
          </Flex>
        </>
      ),
    },
    {
      id: Tabs.CONTACT_FORMS,
      title: "Contact forms",
      onClick: (): void =>
        trackEngagement("Welcome Tutorial: tabbed menu contact forms clicked", EventPlace.WelcomeTutorial),
      content: (
        <ForwardingTab
          image={contactFormsTabImg}
          title="Contact forms"
          description="Add a contact form to your website so visitors can send messages straight to your HelpDesk. "
          cta={{
            name: "Create",
            onClick: () => handleTabCtaClick("Create contact form clicked"),
            href: SettingsRoutes.ContactForms,
            target: "_blank",
          }}
        />
      ),
    },
    {
      id: Tabs.LIVECHAT,
      title: "LiveChat",
      onClick: (): void =>
        trackEngagement("Welcome Tutorial: tabbed menu LiveChat clicked", EventPlace.WelcomeTutorial),
      content: (
        <ForwardingTab
          image={liveChatTabImg}
          title="LiveChat"
          description="Turn unsettled customer chats into manageable tickets with ease."
          cta={{
            name: "Connect",
            onClick: () => handleTabCtaClick("Connect LiveChat clicked"),
            href: SettingsRoutes.LiveChat,
            target: "_blank",
          }}
        />
      ),
    },
    {
      id: Tabs.OPENWIDGET,
      title: "Contact widget",
      onClick: (): void =>
        trackEngagement("Welcome Tutorial: tabbed menu contact widget clicked", EventPlace.WelcomeTutorial),
      content: (
        <ForwardingTab
          image={openWidgetTabImg}
          title="Contact widget"
          description="Allow your visitors to submit forms for diverse goals through the attention-grabbing widget installed on your website. "
          cta={{
            name: "Connect",
            onClick: () => handleTabCtaClick("Connect contact widget clicked"),
            href: SettingsRoutes.OpenWidget,
            target: "_blank",
          }}
        />
      ),
    },
  ];

  useEffect(() => {
    trackEngagement("Welcome tutorial: Forwarding instructions section opened", EventPlace.WelcomeTutorial);
  }, []);

  return (
    <StepScreen
      title="Get the first messages to HelpDesk"
      step={WelcomeTutorialStep.Forwarding}
      description="Bring together all the communication channels your recipients use to contact you."
      onNext={handleNextClick}
      nextButtonText="Go to HelpDesk"
      onBack={handleBackClick}
    >
      <Flex column alignContent="center" justifyContent="center" maxWidth="510px">
        <TabsWrapper>
          <StyledTabList>
            {tabs.map(({ id, title, onClick }) => (
              <StyledTab
                key={id}
                isSelected={activeTab === id}
                onClick={() => {
                  setActiveTab(id);
                  onClick();
                }}
              >
                {title}
              </StyledTab>
            ))}
          </StyledTabList>
        </TabsWrapper>
        <Content>{tabs.find(tab => tab.id === activeTab)?.content}</Content>
      </Flex>
    </StepScreen>
  );
};

const StyledTabList = styled(TabsList)`
  justify-content: space-between;
`;

const StyledTab = styled(Tab)`
  padding-right: ${withVar(SpacingToken.Spacing2)};
  padding-left: ${withVar(SpacingToken.Spacing2)};
`;

const Content = styled.div`
  padding-top: ${withVar(SpacingToken.Spacing7)};
`;
